<script setup lang="ts">
/**
 * Auth modal host — the frame every auth view is rendered into.
 *
 * FE-014 — was a raw `primevue/dialog` with inline styles and a `pi pi-times`
 * glyph; now it is `FairDialog` in the redesign language. The store, the views
 * and the 20+ call sites are untouched: only the frame changed.
 */
import { computed, ref, watch } from 'vue'
import { useAuthModal } from '@/features/auth/store'
import { FairDialog } from '@/ui/primitives/FairDialog'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import fairLogoWordmarkUrl from '@/ui/icons/brand/fair-logo-wordmark.svg'
import './fair-auth-modal.css'

const auth = useAuthModal()

const authModalState = computed(() => auth.authModal.isShow)
const authModalCurrentView = computed(() => auth.authModal.currentView)
const visible = ref(false)

function handleHideAuthModal() {
  useAuthModal().hideAuthModal()
}

// A route guard can open the modal before this component mounts (guest on
// /style-space → Home); without `immediate` that first `isShow` is never seen.
watch(
  () => authModalState.value,
  () => {
    visible.value = authModalState.value
  },
  { immediate: true },
)
</script>

<template>
  <FairDialog
    v-model:visible="visible"
    class="fair-auth-modal"
    width="440px"
    :show-header="false"
    :show-footer="false"
    :closable="false"
    @close="handleHideAuthModal"
  >
    <div class="fair-auth-modal__header">
      <img
        class="fair-auth-modal__logo"
        :src="fairLogoWordmarkUrl"
        alt="FAIR"
      >
      <FairIconButton
        class="fair-auth-modal__close"
        icon="close"
        size="medium"
        aria-label="Close"
        @click="handleHideAuthModal"
      />
    </div>

    <component
      :is="authModalCurrentView"
      @close-modal="handleHideAuthModal"
    />
  </FairDialog>
</template>
